import React, { useEffect, useContext } from 'react'
import CurrentOnlineCourse from './currentOnlineCourse';
import IconTxtBtnArrow from '../../../../../img/icon-txt-btn-arrow.svg';
import CurrentCourseAddOns from './currentCourseAddOns';
import { useSelector } from 'react-redux'

// import context
import { Context } from '../courseTemplates';

/**
 * Current courses component
 * @param {Object} currentCourses - All the current courses
 * @param {string} courseDataJWT - JWT of course data needed for the registration button
 * @param {boolean} canAttendPortionOfClass - If the user category can attend a portion of the class
 */
function CurrentCourses({currentCourses, courseDataJWT, canAttendPortionOfClass}) {

    // get the current active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)

    // get our global context variables
    const value = useContext(Context);
    const { customerId, brightspaceBaseUrl } = value;

    // trigger on first mount and when current course instances is updated
    useEffect(() => {
        // trigger pop up init event, this is needed for some cases of the register
        // button replacement, like cancel-waitlist
        const InitPopUpsEvent = new Event('initPopups');
        document.body.dispatchEvent(InitPopUpsEvent);
    }, []);

    /**
     * Seat number for a course instance
     * @param {Object} courseData - Data for the current course instance row
     * @returns {number}
     */
    const getCurrentSeatNumber = (courseData) => {
        if(courseData.seat){
            if(courseData.seat.seatNumber){
                return courseData.seat.seatNumber
            }
        }
    }

    /**
     * Get the room for a course
     * @param {Object} courseData - Data for the current course instance row
     * @returns {string}
     */
    const getCurrentSeatRoom = (courseData) => {
        if(courseData.seat){
            if(courseData.seat.classroom){
                return courseData.seat.classroom
            }
        }
    }

    /**
     * Get the JSX for the middle column
     * @param {Object} courseData - Data for the current course instance row
     */
    const getMiddleColumn = (courseData) => {
        // if the user is waitlisted
        if(courseData.waitlisted){
            return (
                <>
                    <h5 className="cell-headline">Waitlist Position</h5>
                    <div>{courseData.waitlistCurrentPosition}</div>
                </>
            )
        // if a user has a seat
        } else if (!courseData.sittingInConferenceRoom && courseData.seat) {
            return (
                <>
                    <h5 className="cell-headline">{getCurrentSeatRoom(courseData)}</h5>
                    <div>Seat {getCurrentSeatNumber(courseData)} | <a href="#" className="seating-popup" data-customerid={customerId} data-courseid={courseData.CourseRecordID} data-categoryid={courseData.enrollmentCategoryRecordId}>Edit</a></div>
                </>
            )
        // if the user is sitting in the conference room
        } else if (courseData.sittingInConferenceRoom) {
            return (
                <h5 className="cell-headline">Conference Room</h5>
            )
        }
    }

    /**
     * Get the JSX for the last column
     * @param {Object} courseData - Data for the current course instance row
     */
    const getLastColumn = (courseData) => {
        // if the user is enrolled
        if(courseData.enrolled){
            // if the course has already started
            if((courseData.startDate * 1000) < new Date()){
                return(<i>Course in-progress</i>)
            // if the course hasn't started, the user can cancel registration
            } else {
                return (
                    <a className="magnific-popup text-btn-arrow" data-popuptype="cancel-registration" data-enrollmentid={courseData.enrollmentRecordID} data-seatid={courseData.seat.recordID} data-coursedates={courseData.dateAttended + " Course"} href="">Cancel Registration<span className="link-arrow"><IconTxtBtnArrow/></span></a>
                )
            }
        // if user is waitlisted and is not eligible to register
        } else if (courseData.waitlisted && !courseData.canRegisterFromWaitlist){
            return(
                <a className="magnific-popup text-btn-arrow" data-popuptype="cancel-waitlist" data-waitlistid={courseData.waitlistRecordID} href="">Remove From Waitlist<span className="link-arrow"><IconTxtBtnArrow/></span></a>
            )
        // if user is waitlisted and eligible to register
        } else if(courseData.waitlisted && courseData.canRegisterFromWaitlist){
            // if the course is in the users cart
            if (courseData.canRegister && restrictedEnrollmentCategories && restrictedEnrollmentCategories[courseData.enrollmentCategoryRecordId]){
                return(
                    <p className="waitlist-in-cart">{ restricted_enrollment_categories[courseData.enrollmentCategoryRecordId] }</p>
                )
                //TODO: Register from course waitlist button
            } else if (courseData.WP_PostId){
                return(
                    <>
                        <a
                            data-courseproductid={courseData.WP_PostId}
                            data-customerid={customerId}
                            data-courseid={courseData.CourseRecordID}
                            data-categoryid={courseData.enrollmentCategoryRecordId}
                            data-roomnameoverride={courseData.unassignedSeatingRoomName}
                            data-jwt={courseDataJWT}
                            className={`btn c-btn--light add-course-to-cart ${canAttendPortionOfClass ? 'invoke-portion-of-class-popup seating-popup' : courseData.sittingInConferenceRoom ? 'skip-seating-chart': 'seating-popup'}`}>
                            Register from Waitlist
                        </a>
                        <br/>
                        <a href="#" className="magnific-popup" data-popuptype="cancel-waitlist" data-waitlistid={courseData.waitlistRecordID}>Remove from Waitlist</a>
                    </>
                )
            // if an error occurs
            } else {
                return (
                    <p>An error occurred, registration won't be possible at this time. Please contact an administrator if this persists.</p>
                )
            }
        // if user is waitlisted and not eligible to register
        } else {
            return(
                <a className="magnific-popup text-btn-arrow" data-popuptype="cancel-waitlist" data-waitlistid={courseData.waitlistRecordID} href="">Remove From Waitlist<span className="link-arrow"><IconTxtBtnArrow/></span></a>
            )
        }
    }

// Component JSX return ******************************************************
    return (
        <>
            {currentCourses.length > 0 &&
                <div className="block-panel">
                    <h4 className="block-panel__header h2">Current Dates Attending</h4>
                    <div className="block-panel__content">
                        <div className="table-course-list">
                            {/* Loop through current course instances */}
                            {currentCourses.map((currentCourse, index) => {
                                if(index === 0 && currentCourse.isOnline){
                                    return(<CurrentOnlineCourse currentCourse={currentCourse} brightspaceBaseUrl={brightspaceBaseUrl} activeCourseName={activeCourseName}/>);
                                } else {
                                    return(
                                        <>
                                            <div className="table-course-list__row responsive-grid rg-align-items-center">
                                                <div className="rg-5 rg-sm-12 rg-xs-12">
                                                    <h4 className="cell-headline">{currentCourse.dateAttended}</h4>
                                                    {currentCourse.registeredAs} | <a href="#" className="fancy popup-link add-to-schedule" data-day={currentCourse.startDate} data-methodid={currentCourse.CourseRecordID}>Add to Calendar</a>
                                                </div>
                                                <div className="rg-3 rg-sm-12 rg-xs-12">
                                                    {getMiddleColumn(currentCourse)}
                                                </div>
                                                <div className="rg-4 rg-sm-12 rg-xs-12">
                                                    {getLastColumn(currentCourse)}
                                                </div>
                                                    {/* If the current course has available course add on products or a printed manual */}
                                                    { (currentCourse.enrollmentProductAddOns || currentCourse.hasPrintedManual) &&
                                                        <CurrentCourseAddOns currentCourse={currentCourse} />
                                                    }
                                            </div>
                                        </>
                                    )}}
                                )
                            }
                        </div>
                    </div>
                </div>
            }
        </>
    );
}

export default CurrentCourses;